"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider } from "next-auth/react";
import NextTopLoader from "nextjs-toploader";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { useState } from "react";
import { NotificationToaster } from "@/components/ui/notification-toaster";
import { UploadProgressPopup } from "@/components/ui/upload-progress-popup";
import { installCsrfFetch } from "@/lib/csrf-fetch";
import { makeQueryClient } from "@/lib/query-client";

installCsrfFetch();

// Dùng cho (application)/(auth) — 2 nơi THẬT SỰ cần session (SessionWatcher,
// login-form dùng useSession/signIn, toast lỗi đăng nhập). KHÔNG dùng ở root
// layout: SessionProvider tự fetch /api/auth/session ngay khi mount (kể cả
// refetchInterval={0} cũng không tắt được lần fetch đầu này) — nếu đặt ở
// root thì MỌI route top-level khác (/rate-limited, /lostnfound) cũng bị
// kéo theo gọi API này. Với /rate-limited, điều đó trực tiếp phá vỡ lý do
// trang tồn tại (tránh gọi lại API có thể vẫn đang bị chặn) — nếu API
// /api/auth/session vẫn còn trong cửa sổ bị Cloudflare chặn, mount trang
// /rate-limited lập tức bị chặn lại → redirect vòng lặp vô hạn, không đợi
// đếm ngược. Xem RootProviders bên dưới cho root layout.
//
// KHÔNG render NextTopLoader ở đây — root layout (RootProviders bên dưới)
// đã bọc NGOÀI CÙNG mọi route kể cả (application)/(auth) (Next.js App
// Router lồng layout theo cây), nên 1 instance ở đó là đủ cho toàn app.
// Từng có 2 instance (1 ở đây, 1 ở RootProviders) chồng lên nhau — trên
// mobile 2 bar luôn trùng vị trí top:0 nên không thấy khác biệt, nhưng
// desktop lộ rõ thành 2 thanh loading tách biệt.
export default function Providers({ children }: { children: React.ReactNode }) {
  // 1 client / mount (useState initializer) — không tạo mới mỗi render, không
  // singleton module-level (tránh cache chia sẻ xuyên request trên server).
  const [queryClient] = useState(() => makeQueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider refetchOnWindowFocus={true} refetchInterval={0}>
        {children}
        <NotificationToaster />
        <UploadProgressPopup />
      </SessionProvider>
    </QueryClientProvider>
  );
}

// Root layout (app/layout.tsx) — bọc MỌI route kể cả /rate-limited/lostnfound,
// nên KHÔNG được chứa gì tự ý gọi /api/* lúc mount. installCsrfFetch() chỉ
// patch window.fetch (không tự gọi API), NextTopLoader chỉ là thanh loading
// thuần UI, NuqsAdapter cũng không có side-effect mount (không fetch, không
// gọi API) — cả 3 an toàn dùng toàn cục.
// RepostHub: đẩy `tab=` xuống cuối URL (?tenantId=…&jobId=…&videoId=…&tab=videos) —
// nuqs giữ vị trí cũ của key khi set lại nên phải sắp lại mỗi lần ghi URL.
function processUrlSearchParams(search: URLSearchParams): URLSearchParams {
  if (typeof window === "undefined" || !window.location.pathname.startsWith("/repost-hub")) return search;
  const tab = search.get("tab");
  if (tab === null) return search;
  search.delete("tab");
  search.append("tab", tab);
  return search;
}

export function RootProviders({ children }: { children: React.ReactNode }) {
  return (
    <NuqsAdapter processUrlSearchParams={processUrlSearchParams}>
      <NextTopLoader color="#3b82f6" showSpinner={false} height={3} shadow={false} />
      {children}
    </NuqsAdapter>
  );
}
